import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "./ApiKeys.css";

const UI_FULL = "ui:full";

function getApiBaseUrl() {
  if (window.location.hostname === "localhost") {
    return `http://localhost:5001`;
  }
  return `${window.location.protocol}//${window.location.host}/api`;
}

function getDownloadUrl() {
  return `${getApiBaseUrl()}/mcp-server/download`;
}

function buildMcpConfig(token, format) {
  const baseUrl = getApiBaseUrl();
  const home = format === "cursor" ? "~/mcp_server.py" : "~/mcp_server.py";

  const payload = {
    mcpServers: {
      "support-analysis": {
        command: "python3",
        args: [home],
        env: {
          SUPPORT_API_URL: baseUrl,
          SUPPORT_API_TOKEN: token,
        },
      },
    },
  };

  return JSON.stringify(payload, null, 2);
}

function buildSampleConfig() {
  const baseUrl = getApiBaseUrl();
  return JSON.stringify(
    {
      mcpServers: {
        "support-analysis": {
          command: "python3",
          args: ["~/mcp_server.py"],
          env: {
            SUPPORT_API_URL: baseUrl,
            SUPPORT_API_TOKEN: "YOUR_API_KEY_HERE",
          },
        },
      },
    },
    null,
    2
  );
}

function McpConfigSnippet({ token }) {
  const [activeTab, setActiveTab] = useState("cursor");
  const [copied, setCopied] = useState(false);

  const config = useMemo(
    () => buildMcpConfig(token, activeTab),
    [token, activeTab]
  );

  const handleCopy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(config);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const filename =
    activeTab === "cursor" ? ".cursor/mcp.json" : "claude_desktop_config.json";
  const description =
    activeTab === "cursor"
      ? "Paste into your project's .cursor/mcp.json (or ~/.cursor/mcp.json for global access)."
      : "Merge into ~/Library/Application Support/Claude/claude_desktop_config.json (macOS) or %APPDATA%\\Claude\\claude_desktop_config.json (Windows).";

  return (
    <div className="mcp-config-card">
      <h3>MCP Configuration</h3>
      <p className="mcp-config-description">
        Use this token to connect AI agents to the Support Analysis API.
        If you saved <code>mcp_server.py</code> to a different location,
        update the path in <code>args</code> below.
      </p>
      <div className="mcp-tabs">
        <button
          className={`mcp-tab${activeTab === "cursor" ? " mcp-tab-active" : ""}`}
          onClick={() => setActiveTab("cursor")}
        >
          Cursor
        </button>
        <button
          className={`mcp-tab${activeTab === "claude" ? " mcp-tab-active" : ""}`}
          onClick={() => setActiveTab("claude")}
        >
          Claude Desktop
        </button>
      </div>
      <div className="mcp-tab-info">
        <code>{filename}</code>
        <span>{description}</span>
      </div>
      <pre className="mcp-code-block">{config}</pre>
      <button type="button" className="mcp-copy-btn" onClick={handleCopy}>
        {copied ? "Copied!" : "Copy config"}
      </button>
    </div>
  );
}

const MCP_TOOLS = [
  {
    category: "Ticket Intelligence",
    tools: [
      { name: "get_support_tickets", desc: "List all tickets with AI sentiment scores and root-cause summaries" },
      { name: "get_ticket_details", desc: "Full ticket view: metadata, conversation, and AI analysis" },
      { name: "get_customer_history", desc: "All tickets for a customer to spot repeat issues" },
      { name: "lookup_salesforce_case", desc: "Look up a Salesforce case with AI summary and root cause" },
    ],
  },
  {
    category: "Health & Risk",
    tools: [
      { name: "get_account_health_report", desc: "RAG (Red/Amber/Green) health status across all accounts" },
      { name: "get_weekly_performance_metrics", desc: "Weekly support metrics with optional drill-down" },
    ],
  },
  {
    category: "Root Cause Insights",
    tools: [
      { name: "get_ticket_root_cause_buckets", desc: "AI-categorised root-cause buckets for tickets" },
      { name: "get_chat_root_cause_buckets", desc: "AI-categorised root-cause buckets from chat" },
    ],
  },
  {
    category: "Google Chat Intelligence",
    tools: [
      { name: "list_chat_spaces", desc: "List monitored Google Chat spaces" },
      { name: "search_chat_conversations", desc: "Search chats by space, keyword, or date range" },
      { name: "get_preventive_actions", desc: "AI-recommended preventive actions from chats" },
    ],
  },
  {
    category: "Data Freshness",
    tools: [
      { name: "check_data_sync_status", desc: "Is a Salesforce sync running? When did the last one finish?" },
      { name: "get_sync_history", desc: "Full history of data-sync runs with status and timing" },
    ],
  },
];

function McpSetupGuide() {
  const [open, setOpen] = useState(false);
  const downloadUrl = getDownloadUrl();

  return (
    <div className="mcp-guide">
      <button
        type="button"
        className="mcp-guide-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="mcp-guide-toggle-icon">{open ? "\u25BC" : "\u25B6"}</span>
        MCP Server Setup Guide
      </button>

      {open && (
        <div className="mcp-guide-body">
          <section className="mcp-guide-section">
            <h4>What is this?</h4>
            <p>
              The MCP (Model Context Protocol) server lets AI agents in{" "}
              <strong>Cursor</strong>, <strong>Claude Desktop</strong>, or any
              MCP-compatible client query your support data directly.
              It provides 13 business-friendly tools &mdash; no coding or
              repository access required. Just download one file, install two
              packages, and paste a config.
            </p>
          </section>

          <section className="mcp-guide-section">
            <h4>Setup in 4 steps</h4>
            <ol className="mcp-guide-steps">
              <li>
                <strong>Download <code>mcp_server.py</code></strong>
                <p className="mcp-guide-step-detail">
                  Save it anywhere convenient (e.g. your home folder).
                </p>
                <a
                  href={downloadUrl}
                  className="mcp-download-btn"
                  download="mcp_server.py"
                >
                  Download mcp_server.py
                </a>
              </li>
              <li>
                <strong>Install two Python packages</strong>
                <p className="mcp-guide-step-detail">
                  You need Python 3.10 or newer. Open a terminal and run:
                </p>
                <pre className="mcp-guide-code">pip3 install "mcp[cli]" httpx</pre>
              </li>
              <li>
                <strong>Generate an API key</strong>
                <p className="mcp-guide-step-detail">
                  Use the form below on this page. Select the scopes you need
                  (or check <em>Grant all scopes</em> for full access). After
                  creating the token, a ready-to-paste config will appear with
                  your key pre-filled.
                </p>
              </li>
              <li>
                <strong>Paste the config into your AI client</strong>
                <p className="mcp-guide-step-detail">
                  Copy the JSON below, replace{" "}
                  <code>YOUR_API_KEY_HERE</code> with the token you generated,
                  and save it to the config file for your client:
                </p>
                <pre className="mcp-guide-code">{buildSampleConfig()}</pre>
                <ul className="mcp-guide-sublist">
                  <li>
                    <strong>Cursor</strong> &mdash; Save to{" "}
                    <code>.cursor/mcp.json</code> in your project root (or{" "}
                    <code>~/.cursor/mcp.json</code> for global access).
                  </li>
                  <li>
                    <strong>Claude Desktop</strong> &mdash; Merge into{" "}
                    <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>{" "}
                    (macOS) or{" "}
                    <code>%APPDATA%\Claude\claude_desktop_config.json</code>{" "}
                    (Windows).
                  </li>
                </ul>
                <p className="mcp-guide-step-detail">
                  Tip: When you generate a token below, the config card will
                  have your key already filled in &mdash; just copy and paste.
                </p>
              </li>
            </ol>
            <p>
              That's it. Restart your AI client and the support analysis tools
              will be available to the agent.
            </p>
          </section>

          <section className="mcp-guide-section">
            <h4>Adjusting the file path</h4>
            <p>
              The generated config assumes you saved the file as{" "}
              <code>~/mcp_server.py</code>. If you saved it elsewhere, update
              the <code>args</code> path in the config. For example:
            </p>
            <pre className="mcp-guide-code">{`"args": ["/Users/jane/Downloads/mcp_server.py"]`}</pre>
            <p>
              On Windows, use the full path with double backslashes:
            </p>
            <pre className="mcp-guide-code">{`"args": ["C:\\\\Users\\\\jane\\\\mcp_server.py"]`}</pre>
          </section>

          <section className="mcp-guide-section">
            <h4>Verifying the setup</h4>
            <p>
              To check that everything works before configuring your AI client,
              open a terminal and run:
            </p>
            <pre className="mcp-guide-code">{`SUPPORT_API_URL=${getApiBaseUrl()} \\
SUPPORT_API_TOKEN=your-token-here \\
python3 ~/mcp_server.py`}</pre>
            <p>
              The server communicates over <strong>stdio</strong>, so it won't
              print a URL. If it starts without errors, it's working.
              Press <code>Ctrl+C</code> to stop.
            </p>
          </section>

          <section className="mcp-guide-section">
            <h4>Available tools (13)</h4>
            <p>
              Once connected, your AI agent can call any of these. All
              operations are <strong>read-only</strong> and safe to use.
            </p>
            <div className="mcp-guide-tools">
              {MCP_TOOLS.map((group) => (
                <div key={group.category} className="mcp-guide-tool-group">
                  <h5>{group.category}</h5>
                  {group.tools.map((t) => (
                    <div key={t.name} className="mcp-guide-tool-row">
                      <code>{t.name}</code>
                      <span>{t.desc}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="mcp-guide-section">
            <h4>Scope recommendations</h4>
            <p>Pick scopes that match what the agent needs:</p>
            <ul className="mcp-guide-sublist">
              <li>
                <strong>Read-only analyst</strong> &mdash;{" "}
                <code>tickets:read</code>, <code>rag:read</code>,{" "}
                <code>buckets:read</code>, <code>metrics:read</code>
              </li>
              <li>
                <strong>Chat analysis</strong> &mdash;{" "}
                <code>gchat:read</code>, <code>buckets:read</code>
              </li>
              <li>
                <strong>Full access</strong> &mdash; use <em>Grant all scopes</em>{" "}
                or select <code>ui:full</code> (covers all non-admin scopes)
              </li>
            </ul>
          </section>

          <section className="mcp-guide-section">
            <h4>Troubleshooting</h4>
            <div className="mcp-guide-faq">
              <details>
                <summary>&ldquo;SUPPORT_API_TOKEN is not set&rdquo;</summary>
                <p>
                  The <code>SUPPORT_API_TOKEN</code> environment variable is
                  missing from the config. Make sure the <code>env</code> block
                  in your config file includes the token you generated.
                </p>
              </details>
              <details>
                <summary>&ldquo;ModuleNotFoundError: No module named mcp&rdquo;</summary>
                <p>
                  The Python packages aren't installed. Run{" "}
                  <code>pip3 install "mcp[cli]" httpx</code> in your terminal.
                  Make sure you're using the same Python that your AI client
                  will use (check with <code>which python3</code>).
                </p>
              </details>
              <details>
                <summary>Agent gets &ldquo;Insufficient scope&rdquo; errors</summary>
                <p>
                  The token doesn't include the required scope for the tool
                  being called. Generate a new token with broader scopes, or
                  use <em>Grant all scopes</em>.
                </p>
              </details>
              <details>
                <summary>Agent gets &ldquo;Token expired&rdquo;</summary>
                <p>
                  The JWT has exceeded its lifetime. Generate a new token and
                  update the config. For long-lived setups, use the 365-day
                  lifetime option.
                </p>
              </details>
              <details>
                <summary>Connection refused / timeout</summary>
                <p>
                  The AI client's machine cannot reach the backend server.
                  Verify that <code>SUPPORT_API_URL</code> in the config points
                  to the correct address and that port 5001 is accessible.
                </p>
              </details>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

const TTL_PRESETS = [
  { label: "1 day", seconds: 86400 },
  { label: "7 days", seconds: 604800 },
  { label: "30 days", seconds: 2592000 },
  { label: "90 days", seconds: 7776000 },
  { label: "365 days", seconds: 31536000 },
];

const ApiKeys = () => {
  const [catalog, setCatalog] = useState([]);
  const [allScopesCount, setAllScopesCount] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  const [name, setName] = useState("");
  const [ttlPreset, setTtlPreset] = useState("604800");
  const [customTtl, setCustomTtl] = useState("");
  const [useCustomTtl, setUseCustomTtl] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState(() => new Set());
  const [grantAllScopes, setGrantAllScopes] = useState(false);
  const [adminSecret, setAdminSecret] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/auth/scopes")
      .then((res) => {
        if (!cancelled && res.data?.scopes) {
          setCatalog(res.data.scopes);
          if (typeof res.data.all_scopes_count === "number") {
            setAllScopesCount(res.data.all_scopes_count);
          } else {
            setAllScopesCount(res.data.scopes.length);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogError(
            err.response?.data?.error ||
              "Could not load scope list. Is the backend running?"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleScope = useCallback((id) => {
    setGrantAllScopes(false);
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (id === UI_FULL) {
        if (next.has(UI_FULL)) {
          next.delete(UI_FULL);
        } else {
          return new Set([UI_FULL]);
        }
      } else {
        next.delete(UI_FULL);
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const ttlSeconds = useCallback(() => {
    if (useCustomTtl) {
      const n = parseInt(customTtl, 10);
      return Number.isFinite(n) ? n : 604800;
    }
    return parseInt(ttlPreset, 10) || 604800;
  }, [useCustomTtl, customTtl, ttlPreset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!grantAllScopes) {
      const scopes = Array.from(selectedScopes);
      if (scopes.length === 0) {
        setError(
          "Select at least one scope, turn on Grant all scopes, or enable Full UI access."
        );
        return;
      }
    }
    const ttl = ttlSeconds();
    if (ttl < 60 || ttl > 365 * 86400) {
      setError("TTL must be between 60 seconds and 365 days.");
      return;
    }
    setSubmitting(true);
    try {
      const headers = {};
      if (adminSecret.trim()) {
        headers["X-Admin-Secret"] = adminSecret.trim();
      }
      const body = {
        name: name.trim() || "api-token",
        ttl_seconds: ttl,
      };
      if (grantAllScopes) {
        body.all_scopes = true;
      } else {
        body.scopes = Array.from(selectedScopes);
      }
      const res = await axios.post("/auth/tokens", body, { headers });
      setResult(res.data);
      setAdminSecret("");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Failed to create token.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToken = () => {
    if (result?.token && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(result.token);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="apikeys-container">
        <div className="apikeys-header">
          <h1>API keys</h1>
          <p>
            Create JWT access tokens with scopes. Tokens are shown only once.
            The server does not store them; revocation is by expiry or rotating{" "}
            <code>API_JWT_SECRET</code>.
          </p>
        </div>

        <div className="apikeys-note">
          <strong>Authorization.</strong> Minting requires either the{" "}
          <strong>X-Admin-Secret</strong> header (enter below) or a Bearer token
          that includes the <code>admin:tokens</code> scope (e.g. from{" "}
          <code>REACT_APP_API_TOKEN</code> via the axios interceptor).
        </div>

        <McpSetupGuide />

        {loadingCatalog && (
          <p className="apikeys-loading">Loading scopes…</p>
        )}
        {catalogError && (
          <div className="apikeys-error" style={{ marginTop: 0 }}>
            {catalogError}
          </div>
        )}

        {!loadingCatalog && catalog.length > 0 && (
          <form className="apikeys-form" onSubmit={handleSubmit}>
            <label htmlFor="apikey-name">Name (label, stored in token)</label>
            <input
              id="apikey-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. partner-integration"
              autoComplete="off"
            />

            <label htmlFor="apikey-ttl">Lifetime</label>
            <select
              id="apikey-ttl"
              value={ttlPreset}
              onChange={(e) => setTtlPreset(e.target.value)}
              disabled={useCustomTtl}
            >
              {TTL_PRESETS.map((p) => (
                <option key={p.seconds} value={p.seconds}>
                  {p.label}
                </option>
              ))}
            </select>
            <label
              style={{ marginTop: 10, fontWeight: 500 }}
              htmlFor="apikey-custom-ttl"
            >
              <input
                id="apikey-custom-ttl"
                type="checkbox"
                checked={useCustomTtl}
                onChange={(e) => setUseCustomTtl(e.target.checked)}
              />{" "}
              Custom TTL (seconds)
            </label>
            {useCustomTtl && (
              <input
                type="text"
                inputMode="numeric"
                value={customTtl}
                onChange={(e) => setCustomTtl(e.target.value)}
                placeholder="604800"
              />
            )}

            <label>Scopes</label>
            <label className="apikeys-all-scopes-master">
              <input
                type="checkbox"
                checked={grantAllScopes}
                onChange={(e) => setGrantAllScopes(e.target.checked)}
              />
              <div className="apikeys-all-scopes-text">
                <strong>Grant all scopes</strong>
                <p>
                  Full access to every API scope the server defines, including{" "}
                  <code>ui:full</code>, <code>admin:tokens</code>, and all read/write
                  capabilities. Use for trusted integrations only.
                </p>
                {grantAllScopes && allScopesCount != null && (
                  <div className="apikeys-all-scopes-count">
                    {allScopesCount} scopes will be embedded in the JWT.
                  </div>
                )}
              </div>
            </label>
            <div
              className={`apikeys-scopes${
                grantAllScopes ? " apikeys-scopes-disabled" : ""
              }`}
            >
              {catalog.map((s) => (
                <label key={s.id} className="apikeys-scope-row">
                  <input
                    type="checkbox"
                    checked={grantAllScopes || selectedScopes.has(s.id)}
                    onChange={() => toggleScope(s.id)}
                  />
                  <div className="apikeys-scope-text">
                    <strong>{s.label}</strong>
                    <span>{s.description}</span>
                    <div className="apikeys-scope-id">{s.id}</div>
                  </div>
                </label>
              ))}
            </div>

            <label htmlFor="apikey-admin">Admin secret (optional)</label>
            <input
              id="apikey-admin"
              type="password"
              value={adminSecret}
              onChange={(e) => setAdminSecret(e.target.value)}
              placeholder="API_KEY_ADMIN_SECRET — only if not using Bearer admin token"
              autoComplete="new-password"
            />

            <div className="apikeys-actions">
              <button
                type="submit"
                className="apikeys-submit"
                disabled={submitting}
              >
                {submitting ? "Creating…" : "Create token"}
              </button>
            </div>
          </form>
        )}

        {error && <div className="apikeys-error">{error}</div>}

        {result && (
          <div className="apikeys-result">
            <h2>Token created</h2>
            <p className="apikeys-meta">
              Expires: {result.expires_at} · Name: {result.name}
              {Array.isArray(result.scopes) && (
                <>
                  {" "}
                  · {result.scopes.length} scope
                  {result.scopes.length === 1 ? "" : "s"}
                </>
              )}
            </p>
            {Array.isArray(result.scopes) && result.scopes.length > 0 && (
              <details className="apikeys-scope-details">
                <summary>View all scope ids in this token</summary>
                <pre className="apikeys-scope-list">
                  {result.scopes.join("\n")}
                </pre>
              </details>
            )}
            <textarea
              className="apikeys-token-box"
              readOnly
              value={result.token}
            />
            <button type="button" className="apikeys-copy" onClick={copyToken}>
              Copy to clipboard
            </button>

            <McpConfigSnippet token={result.token} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
